import { useMemo } from 'react';

import {
  useAvailableIntegrationsQuery,
  useConnectedAccountsQuery,
} from '@/features/integrations/integrations.query';
import {
  type CachedConnectedAccount,
  type IntegrationInfo,
  type IntegrationState,
} from '@/features/integrations/integrations.types';
import {
  DEVICE_INTEGRATION_BETA_LABELS,
  isConnectableDeviceToolkit,
  useDevicePermissionGrants,
} from '@/integrations/device-integrations';

export function useIntegrationState(includeUnsupportedPlatforms = false) {
  const {
    data: availableIntegrations,
    error: availableIntegrationsError,
    refetch: refetchAvailableIntegrations,
  } = useAvailableIntegrationsQuery(includeUnsupportedPlatforms);
  const {
    data: connectedAccounts,
    error: connectedAccountsError,
    refetch: refetchConnectedAccounts,
  } = useConnectedAccountsQuery();

  // Only an error when there's nothing to render — a failed background
  // refetch with cached data keeps showing the data
  const loadError =
    (!availableIntegrations && availableIntegrationsError) ||
    (!connectedAccounts && connectedAccountsError) ||
    null;

  // Card visibility for device integrations is the backend catalog's call;
  const {
    applecontacts: contactsGranted,
    applereminders: remindersGranted,
    terminal: terminalEnabled,
  } = useDevicePermissionGrants();

  const integrationStates: IntegrationState[] = useMemo(() => {
    if (!availableIntegrations) return [];

    const deviceGranted: Record<string, boolean> = {
      applecontacts: contactsGranted,
      applereminders: remindersGranted,
      terminal: terminalEnabled,
    };

    return availableIntegrations.map((info: IntegrationInfo) => {
      const accounts =
        connectedAccounts?.filter(
          (acc) => acc.toolkit_name === info.toolkit_name
        ) ?? [];

      if (info.on_device && isConnectableDeviceToolkit(info.toolkit_name)) {
        return {
          info,
          accounts,
          isConnected: deviceGranted[info.toolkit_name] ?? false,
          ...DEVICE_INTEGRATION_BETA_LABELS[info.toolkit_name],
        };
      }

      const isConnected = accounts.some(
        (acc) =>
          acc.connection_status === 'connected' ||
          acc.connection_status === 'probing'
      );

      return {
        info,
        accounts,
        isConnected,
      };
    });
  }, [
    availableIntegrations,
    connectedAccounts,
    contactsGranted,
    remindersGranted,
    terminalEnabled,
  ]);

  const hasAnyConnected = useMemo(
    () => integrationStates.some((s) => s.isConnected),
    [integrationStates]
  );

  const accountsByToolkit = useMemo(() => {
    if (!connectedAccounts) return {};
    return connectedAccounts.reduce(
      (acc, account) => {
        const key = account.toolkit_name;
        if (!acc[key]) acc[key] = [];
        acc[key].push(account);
        return acc;
      },
      {} as Record<string, CachedConnectedAccount[]>
    );
  }, [connectedAccounts]);

  return {
    availableIntegrations,
    connectedAccounts,
    integrationStates,
    hasAnyConnected,
    accountsByToolkit,
    loadError,
    refetchAvailableIntegrations,
    refetchConnectedAccounts,
  };
}
